// ─── src/components/search/ImmersiveSearch.jsx ────────────────────────────────
// Full-screen immersive search overlay.
// Two-step guided flow: location → category → navigate to results.
// Reuses existing routing callbacks and geo/listing data, no new backend.
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../lib/supabaseClient";
import { COUNTRIES as GEO_COUNTRIES, REGIONS, VENDOR_CATEGORIES, getRegionSlugByName } from "../../data/geo";
import { LOCATIONS as STATIC_LOCATIONS } from "../../data/globalVendors";
import { resolveCountrySlug } from "../../utils/countrySlug";
import AuraImmersiveWorkspace from "../../chat/AuraImmersiveWorkspace";
import { useAura } from "../../stores/auraCore";

// ── Design tokens ─────────────────────────────────────────────────────────────
const GD   = "var(--font-heading-primary)";
const NU   = "var(--font-body)";
const BG   = "#0a0906";
const CARD = "#0f0e0b";
const CARD_H = "#161410";
const WHITE  = "#f5f0e8";
const MUTED  = "rgba(245,240,232,0.60)";
const GOLD   = "#d39a21";
const GOLD_D = "rgba(211,154,33,0.28)";

// ── Category icons (dark-stroke for overlay) ──────────────────────────────────
const CAT_ICONS = {
  "wedding-venues":   (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/></svg>,
  "wedding-planners": (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  "photographers":    (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  "videographers":    (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  "florists":         (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c0 0 0-3 0-6"/><path d="M9 18c-2 0-4-1.5-4-4 0-2 2-3.5 4-3.5.5-2 2-3.5 3-3.5s2.5 1.5 3 3.5c2 0 4 1.5 4 3.5 0 2.5-2 4-4 4"/><path d="M12 8c0-2 1-4 3-5"/><path d="M12 8c0-2-1-4-3-5"/></svg>,
  "caterers":         (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  "wedding-cakes":    (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 18h16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z"/><path d="M6 14h12v4H6z"/><path d="M8 10h8v4H8z"/><path d="M12 3v3"/><circle cx="12" cy="2" r="1"/></svg>,
  "hair-makeup":      (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="5"/><path d="M12 13v8"/><path d="M9 18h6"/></svg>,
  "entertainment":    (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  "stationery":       (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  "bridal-wear":      (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C9 2 7 5 7 8c0 2 1 3 2 4l-3 10h12l-3-10c1-1 2-2 2-4 0-3-2-6-5-6z"/><path d="M9 22h6"/></svg>,
  "jewellers":        (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="14" r="6"/><path d="M12 8V2"/><path d="M8 10l-3-5"/><path d="M16 10l3-5"/></svg>,
  "transport":        (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14v-5H5v5z"/><path d="M2 12h20"/><path d="M5 12V7c0-1.7 1.3-3 3-3h8c1.7 0 3 1.3 3 3v5"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>,
  "event-design":     (c) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
};

const CATS_PER_ROW = 7;

// Module-level cache for live countries from Supabase.
// Survives component mount/unmount, avoids a DB query on every overlay open.
// Expires after 5 minutes so new listings eventually appear.
const _liveCountriesCache = { data: null, at: 0 };
const LIVE_CACHE_TTL = 5 * 60 * 1000;
function readLiveCache() {
  return _liveCountriesCache.data && Date.now() - _liveCountriesCache.at < LIVE_CACHE_TTL
    ? _liveCountriesCache.data
    : null;
}
function writeLiveCache(data) {
  _liveCountriesCache.data = data;
  _liveCountriesCache.at   = Date.now();
}


// ── Editorial curation ────────────────────────────────────────────────────────
// These slugs reference GEO_COUNTRIES, not hardcoded data, just which
// destinations appear as featured tiles.
const FEATURED_SLUGS = [
  "italy", "france", "greece", "spain", "england",
  "portugal", "croatia", "scotland", "usa",
];

// ── Destination tile images ────────────────────────────────────────────────────
const TILE_IMAGES = {
  "italy":    "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=800&q=80",
  "france":   "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=800&q=80",
  "greece":   "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=800&q=80",
  "spain":    "https://images.unsplash.com/photo-1543785734-4b6e564642f8?auto=format&fit=crop&w=800&q=80",
  "england":  "https://images.unsplash.com/photo-1529655683826-aba9b3e77383?auto=format&fit=crop&w=800&q=80",
  "portugal": "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=800&q=80",
  "croatia":  "https://images.unsplash.com/photo-1555990793-da11153b2473?auto=format&fit=crop&w=800&q=80",
  "scotland": "https://images.unsplash.com/photo-1506377585622-bedcbb027afc?auto=format&fit=crop&w=800&q=80",
  "usa":      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80",
};

// ── Region click tracking ─────────────────────────────────────────────────────
// Reads/writes lwd:region-clicks in localStorage. Per-device demand signal.
// Falls back to listingCount on first visit or when localStorage unavailable.
function readRegionClickCounts() {
  try {
    return JSON.parse(localStorage.getItem("lwd:region-clicks") || "{}");
  } catch { return {}; }
}
function recordRegionClick(slug) {
  try {
    const counts = readRegionClickCounts();
    counts[slug] = (counts[slug] || 0) + 1;
    localStorage.setItem("lwd:region-clicks", JSON.stringify(counts));
  } catch { /* storage unavailable, silent */ }
}

// ── Featured region tiles, demand-aware ─────────────────────────────────────
// Top 2 primary-priority regions per featured country.
// Primary sort: local click count (what this user explores most).
// Tiebreaker: listingCount (editorial depth).
// No hardcoded slug list, self-updates as regions gain content or priority.
function buildFeaturedRegionTiles() {
  const clicks = readRegionClickCounts();
  return FEATURED_SLUGS.flatMap((countrySlug) =>
    REGIONS
      .filter((r) =>
        r.countrySlug === countrySlug &&
        r.slug !== "all" &&
        r.name &&
        (r.priorityLevel === "primary" || r.priorityLevel === 1)
      )
      .sort((a, b) => {
        const clickDiff = (clicks[b.slug] || 0) - (clicks[a.slug] || 0);
        return clickDiff !== 0 ? clickDiff : (b.listingCount || 0) - (a.listingCount || 0);
      })
      .slice(0, 2)
  );
}

// Photo backgrounds for known regions, optional, new regions appear without one.
const REGION_TILE_IMAGES = {
  "tuscany":       "https://images.unsplash.com/photo-1568797629192-78a4d6b2e9cc?auto=format&fit=crop&w=800&q=80",
  "lake-como":     "https://images.unsplash.com/photo-1533165985060-e9c929b2e5a4?auto=format&fit=crop&w=800&q=80",
  "amalfi-coast":  "https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?auto=format&fit=crop&w=800&q=80",
  "venice":        "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=800&q=80",
  "london":        "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=800&q=80",
  "surrey":        "https://images.unsplash.com/photo-1560969184-10fe8719e047?auto=format&fit=crop&w=800&q=80",
  "provence":      "https://images.unsplash.com/photo-1534430480872-3498386e7856?auto=format&fit=crop&w=800&q=80",
  "paris":         "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=800&q=80",
  "loire-valley":  "https://images.unsplash.com/photo-1504215680853-026ed2a45def?auto=format&fit=crop&w=800&q=80",
  "marrakech":     "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?auto=format&fit=crop&w=800&q=80",
  "cape-town":     "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=800&q=80",
  "dubai":         "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=800&q=80",
};

// ── Style-led discovery tiles (Mode 2) ───────────────────────────────────────
// Opens Aura chat with a pre-seeded style query, bypasses location entirely.
// Designed to capture couples who lead with aesthetic, not destination.
const STYLE_TILES = [
  { id: "vineyard",        label: "Vineyard",        query: "I want a vineyard wedding, rolling hills, golden light, outdoor ceremony" },
  { id: "coastal-drama",   label: "Coastal Drama",   query: "I want something dramatic by the sea, clifftop or coastal, vivid blue water" },
  { id: "rustic-luxe",     label: "Rustic Luxe",     query: "rustic luxury, stone barn or farmhouse, natural materials but still elevated" },
  { id: "old-money",       label: "Old Money",       query: "understated elegance, old money, country estate or manor, nothing showy" },
  { id: "wild-remote",     label: "Wild & Remote",   query: "wild and remote, dramatic landscape, mountains or highlands, away from everything" },
  { id: "historic",        label: "Historic",        query: "historic venue, ancient or medieval, chapel ceremony, ceremony in a real castle or palazzo" },
  { id: "urban-prestige",  label: "Urban Prestige",  query: "urban luxury, city wedding, rooftop or grand ballroom, architectural backdrop" },
  { id: "sun-social",      label: "Sun & Social",    query: "warm sun, guests spilling outside, long lunch feel, relaxed and joyful" },
];

// Parse a free-text query for recognisable region or country names.
// Returns { label, countrySlug, regionSlug } or null.
// Used to pre-set location when initialQuery is supplied, so typing
// "wedding photographers in Italy" and clicking Aura skips step 0.
function parseLocationFromQuery(query) {
  if (!query) return null;
  const q = query.toLowerCase();

  // Regions first, more specific match wins (e.g. "Tuscany" before "Italy")
  for (const region of REGIONS) {
    if (!region.slug || region.slug === "all" || !region.name || !region.countrySlug) continue;
    if (q.includes(region.name.toLowerCase())) {
      return { label: region.name, countrySlug: region.countrySlug, regionSlug: region.slug };
    }
  }

  // Countries
  for (const country of GEO_COUNTRIES) {
    if (q.includes(country.name.toLowerCase())) {
      return { label: country.name, countrySlug: country.slug, regionSlug: null };
    }
  }

  return null;
}

// Parse a free-text query for category intent.
// Conservative keyword list, only unambiguous terms to avoid false matches.
const CATEGORY_KEYWORDS = [
  { slug: "wedding-venues",   keywords: ["venue", "venues", "chapel", "castle", "estate", "manor", "villa", "barn", "chateau", "château"] },
  { slug: "wedding-planners", keywords: ["planner", "planners", "coordinator", "coordinators"] },
  { slug: "photographers",    keywords: ["photographer", "photographers", "photography"] },
  { slug: "videographers",    keywords: ["videographer", "videographers", "videography", "filmmaker"] },
  { slug: "florists",         keywords: ["florist", "florists", "florist", "floral"] },
  { slug: "caterers",         keywords: ["caterer", "caterers", "catering"] },
  { slug: "wedding-cakes",    keywords: ["wedding cake", "wedding cakes"] },
  { slug: "hair-makeup",      keywords: ["hair and makeup", "hair & makeup", "makeup artist", "hair stylist"] },
  { slug: "entertainment",    keywords: ["live band", "musician", "musicians", "entertainment"] },
  { slug: "bridal-wear",      keywords: ["wedding dress", "wedding gown", "bridal wear", "bridal boutique"] },
  { slug: "event-design",     keywords: ["event design", "wedding décor", "wedding decor"] },
  { slug: "transport",        keywords: ["wedding car", "wedding cars", "limousine"] },
  { slug: "stationery",       keywords: ["stationery", "invitations"] },
  { slug: "jewellers",        keywords: ["jeweller", "jewellers", "jewellery", "jewelry", "wedding ring", "wedding rings"] },
];

function parseCategoryFromQuery(query) {
  if (!query) return null;
  const q = query.toLowerCase();
  for (const { slug, keywords } of CATEGORY_KEYWORDS) {
    if (keywords.some((k) => q.includes(k))) return slug;
  }
  return null;
}

const CATEGORIES = [
  {
    id: "venues", label: "Venues", slug: "wedding-venues",
    tagline: "The spaces that define the day",
    img: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "planners", label: "Planners", slug: "wedding-planners",
    tagline: "Vision, craft, and calm authority",
    img: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "photographers", label: "Photographers", slug: "photographers",
    tagline: "Light, feeling, and memory",
    img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "videographers", label: "Videographers", slug: "videographers",
    tagline: "Motion, music, and emotion",
    img: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "florists", label: "Florists", slug: "florists",
    tagline: "Bloom, colour, and scent",
    img: "https://images.unsplash.com/photo-1487530811015-780e12b74c63?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "caterers", label: "Caterers", slug: "caterers",
    tagline: "Cuisine that becomes memory",
    img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "cakes", label: "Wedding Cakes", slug: "wedding-cakes",
    tagline: "The centrepiece of sweetness",
    img: "https://images.unsplash.com/photo-1535254973040-607b474cb50d?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "hair-makeup", label: "Hair & Makeup", slug: "hair-makeup",
    tagline: "Confidence for your moment",
    img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "entertainment", label: "Entertainment", slug: "entertainment",
    tagline: "The moments between the moments",
    img: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "bridal-wear", label: "Bridal Wear", slug: "bridal-wear",
    tagline: "The dress that tells your story",
    img: "https://images.unsplash.com/photo-1594552072238-b8a33785b6cd?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "event-design", label: "Styling & Décor", slug: "event-design",
    tagline: "Space, texture, and intention",
    img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "transport", label: "Luxury Transport", slug: "transport",
    tagline: "The arrival that sets the tone",
    img: "https://images.unsplash.com/photo-1525338078858-d762b5e32f2c?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "stationery", label: "Stationery", slug: "stationery",
    tagline: "First impressions, on paper",
    img: "https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "jewellers", label: "Jewellers", slug: "jewellers",
    tagline: "The rings that last a lifetime",
    img: "https://images.unsplash.com/photo-1515405295579-ba7b45403062?auto=format&fit=crop&w=900&q=80",
  },
];

// ── Category-specific refinement options ──────────────────────────────────────
const REFINEMENT_CONFIG = {
  "venues": [
    { label: "Style",   key: "style",   options: ["Romantic","Historic","Rustic Luxe","Coastal","Vineyard","Intimate","Modern"] },
    { label: "Guests",  key: "guests",  options: ["Just us","Up to 50","50–100","100–200","200+"] },
    { label: "Setting", key: "setting", options: ["Indoor","Outdoor","Both"] },
    { label: "Budget",  key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "planners": [
    { label: "Service",  key: "style",   options: ["Full Planning","Partial Planning","Day-of Coordination","Destination Wedding"] },
    { label: "Style",    key: "setting", options: ["Luxury","Intimate","Modern","Cultural","Outdoor"] },
    { label: "Guests",   key: "guests",  options: ["Up to 50","50–100","100–200","200+"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "photographers": [
    { label: "Style",    key: "style",   options: ["Documentary","Fine Art","Editorial","Romantic","Dark & Moody","Bright & Airy"] },
    { label: "Coverage", key: "guests",  options: ["Ceremony Only","Half Day","Full Day","Multi-Day"] },
    { label: "Format",   key: "setting", options: ["Photos Only","Film Only","Photos + Film"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "videographers": [
    { label: "Style",    key: "style",   options: ["Cinematic","Documentary","Short Film","Drone Focus","Editorial"] },
    { label: "Coverage", key: "guests",  options: ["Ceremony Only","Half Day","Full Day","Multi-Day"] },
    { label: "Delivery", key: "setting", options: ["Highlight Reel","Feature Film","Social Reels","All Formats"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "florists": [
    { label: "Style",  key: "style",   options: ["Garden & Wild","Opulent","Minimalist","Tropical","Classic White","Boho"] },
    { label: "Scope",  key: "setting", options: ["Bridal Bouquet","Ceremony","Reception","Full Venue"] },
    { label: "Season", key: "guests",  options: ["Spring","Summer","Autumn","Winter","Any Season"] },
    { label: "Budget", key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "caterers": [
    { label: "Style",    key: "style",   options: ["Plated","Buffet","Family Style","Cocktail","BBQ"] },
    { label: "Dietary",  key: "setting", options: ["Standard","Vegetarian","Vegan","Mixed","Kosher / Halal"] },
    { label: "Guests",   key: "guests",  options: ["Up to 50","50–100","100–200","200+"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "wedding-cakes": [
    { label: "Style",     key: "style",   options: ["Classic Tiered","Naked","Floral","Sculpted","Modern","Minimalist"] },
    { label: "Flavour",   key: "setting", options: ["Traditional","Chocolate","Lemon","Floral","Fruit","Surprise Me"] },
    { label: "Servings",  key: "guests",  options: ["Up to 50","50–100","100–200","200+"] },
    { label: "Dietary",   key: "budget",  options: ["Standard","Gluten-Free","Vegan","Mixed"] },
  ],
  "hair-makeup": [
    { label: "Look",     key: "style",   options: ["Natural","Glamorous","Editorial","Vintage","Dewy","Bold Lip"] },
    { label: "Service",  key: "setting", options: ["Bride Only","Bridal Party","Both","Plus Trial"] },
    { label: "On the Day", key: "guests", options: ["Morning Only","Full Day","Touch-ups Included"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "entertainment": [
    { label: "Style",    key: "style",   options: ["Live Band","DJ","Jazz Trio","Orchestra","Solo Artist","Choir"] },
    { label: "Timing",   key: "guests",  options: ["Ceremony","Cocktail Hour","Reception","All Day"] },
    { label: "Setting",  key: "setting", options: ["Indoor","Outdoor","Both"] },
    { label: "Budget",   key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "bridal-wear": [
    { label: "Silhouette", key: "style",   options: ["Ball Gown","A-Line","Mermaid","Minimalist","Vintage","Couture"] },
    { label: "Service",    key: "setting", options: ["Off The Rack","Made To Measure","Bespoke","Alterations"] },
    { label: "Budget",     key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "event-design": [
    { label: "Style",   key: "style",   options: ["Opulent","Romantic","Modern","Bohemian","Minimalist","Garden"] },
    { label: "Scope",   key: "setting", options: ["Ceremony","Reception","Full Venue","Florals Included"] },
    { label: "Guests",  key: "guests",  options: ["Up to 50","50–100","100–200","200+"] },
    { label: "Budget",  key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "transport": [
    { label: "Type",    key: "style",   options: ["Rolls-Royce","Classic Car","Helicopter","Boat","Vintage Bus","Horse & Carriage"] },
    { label: "Usage",   key: "setting", options: ["Ceremony Transfer","Guest Shuttle","Full Day","Airport Run"] },
    { label: "Scale",   key: "guests",  options: ["Just the Couple","Small Group","Large Group"] },
    { label: "Budget",  key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "stationery": [
    { label: "Style",  key: "style",   options: ["Letterpress","Engraved","Foil","Watercolour","Minimalist","Illustrated"] },
    { label: "Scope",  key: "setting", options: ["Invitations Only","Full Suite","Day-of Items","Digital"] },
    { label: "Budget", key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
  "jewellers": [
    { label: "Type",    key: "style",   options: ["Engagement Rings","Wedding Bands","Bespoke","Vintage","Diamonds","Coloured Gems"] },
    { label: "Service", key: "setting", options: ["Ready To Wear","Custom Design","Restoration","Both Rings"] },
    { label: "Budget",  key: "budget",  options: ["Flexible","Mid-range £££","Luxury ££££"] },
  ],
};

// ── Suggestion builder ────────────────────────────────────────────────────────
function buildSuggestions(q, liveCountries) {
  if (!q) return [];
  const out = [];
  const seen = new Set();

  const push = (label, countrySlug, regionSlug) => {
    if (!seen.has(label)) { seen.add(label); out.push({ label, countrySlug, regionSlug }); }
  };

  // Live countries first
  liveCountries?.forEach((c) => {
    if (c.label.toLowerCase().includes(q)) push(c.label, c.slug, null);
  });

  // Regions/cities from static geo data
  STATIC_LOCATIONS.forEach((group) => {
    group.items?.forEach((loc) => {
      const cSlug = resolveCountrySlug(loc.country);
      loc.cities?.forEach((city) => {
        if (city.toLowerCase().includes(q) && cSlug) {
          const rSlug = getRegionSlugByName(city);
          push(`${city}, ${loc.country}`, cSlug, rSlug !== "all" ? rSlug : null);
        }
      });
      if (loc.country.toLowerCase().includes(q) && cSlug) {
        push(loc.country, cSlug, null);
      }
    });
  });

  return out.slice(0, 8);
}

// ═════════════════════════════════════════════════════════════════════════════
// ── Country context chip system ───────────────────────────────────────────────
//
// ARCHITECTURE
// ─────────────────────────────────────────────────────────────────────────────
// Every country page shows three rows:
//   Row 0  "Region"          , country-specific regions (defined per country below)
//   Row 1  "Type of wedding" , shared across all countries
//   Row 2  "Venue style"     , shared across all countries (tuned per country later)
//
// To add a new country: add one entry to COUNTRY_REGISTRY with:
//   slug        , matches the countrySlug passed in from the page
//   displayLabel, used in the heading ("Plan your luxury weddings in…")
//   regions     , array of { label, region } chips for row 0
//   venueStyle  , optional override of SHARED_VENUE_STYLE_ROW.chips (future pass)
//
// buildCountryConfig(slug) returns a fully resolved { displayLabel, rows } object
// ready for the renderer. Falls back to null if the country isn't registered.
// ─────────────────────────────────────────────────────────────────────────────

// ── Shared rows (rows 1 and 2, identical across all countries) ───────────────
const SHARED_WEDDING_TYPE_ROW = {
  rowLabel:      "Type of wedding",
  summaryFormat: "sentence",
  chips: [
    { label: "Country Wedding",  style: "country"     },
    { label: "City Wedding",     style: "city"        },
    { label: "Garden Wedding",   setting: "garden"    },
    { label: "Destination",      style: "destination" },
    { label: "Elopement",        guests: "elopement"  },
    { label: "Weekend Retreat",  style: "estate"      },
  ],
};

const SHARED_VENUE_STYLE_ROW = {
  rowLabel:      "Venue style",
  summaryFormat: "title-setting",
  chips: [
    { label: "Castle",        style: "castle"        },
    { label: "Barn",          style: "barn"          },
    { label: "Coastal",       setting: "coastal"     },
    { label: "Country House", style: "country-house" },
    { label: "Manor House",   style: "manor"         },
    { label: "Vineyard",      style: "vineyard"      },
  ],
};

// ── Per-country registry ───────────────────────────────────────────────────────
// Add one entry per country. Only `slug`, `displayLabel`, and `regions` required.
const COUNTRY_REGISTRY = [
  {
    slug:         "england",
    displayLabel: "the UK",
    regions: [
      { label: "London",        region: "london"        },
      { label: "Cotswolds",     region: "cotswolds"     },
      { label: "Cornwall",      region: "cornwall"      },
      { label: "Yorkshire",     region: "yorkshire"     },
      { label: "Lake District", region: "lake-district" },
      { label: "Devon",         region: "devon"         },
      { label: "Kent",          region: "kent"          },
    ],
  },
  {
    slug:         "italy",
    displayLabel: "Italy",
    regions: [
      { label: "Tuscany",      region: "tuscany"      },
      { label: "Amalfi Coast", region: "amalfi-coast" },
      { label: "Lake Como",    region: "lake-como"    },
      { label: "Sicily",       region: "sicily"       },
      { label: "Venice",       region: "venice"       },
      { label: "Rome",         region: "rome"         },
      { label: "Puglia",       region: "puglia"       },
    ],
  },
  {
    slug:         "france",
    displayLabel: "France",
    regions: [
      { label: "Paris",         region: "paris"        },
      { label: "Provence",      region: "provence"     },
      { label: "Dordogne",      region: "dordogne"     },
      { label: "Loire Valley",  region: "loire-valley" },
      { label: "Côte d'Azur",   region: "cote-d-azur"  },
      { label: "Bordeaux",      region: "bordeaux"     },
    ],
  },
  {
    slug:         "greece",
    displayLabel: "Greece",
    regions: [
      { label: "Santorini", region: "santorini" },
      { label: "Mykonos",   region: "mykonos"   },
      { label: "Athens",    region: "athens"    },
      { label: "Crete",     region: "crete"     },
      { label: "Corfu",     region: "corfu"     },
      { label: "Rhodes",    region: "rhodes"    },
    ],
  },
  {
    slug:         "spain",
    displayLabel: "Spain",
    regions: [
      { label: "Barcelona",  region: "barcelona"  },
      { label: "Andalusia",  region: "andalusia"  },
      { label: "Ibiza",      region: "ibiza"      },
      { label: "Madrid",     region: "madrid"     },
      { label: "Valencia",   region: "valencia"   },
      { label: "Mallorca",   region: "mallorca"   },
    ],
  },
  {
    slug:         "portugal",
    displayLabel: "Portugal",
    regions: [
      { label: "Lisbon",       region: "lisbon"       },
      { label: "Algarve",      region: "algarve"      },
      { label: "Douro Valley", region: "douro-valley" },
      { label: "Porto",        region: "porto"        },
      { label: "Alentejo",     region: "alentejo"     },
    ],
  },
  {
    slug:         "croatia",
    displayLabel: "Croatia",
    regions: [
      { label: "Dubrovnik", region: "dubrovnik" },
      { label: "Split",     region: "split"     },
      { label: "Hvar",      region: "hvar"      },
      { label: "Istria",    region: "istria"    },
      { label: "Brač",      region: "brac"      },
    ],
  },
  {
    slug:         "scotland",
    displayLabel: "Scotland",
    regions: [
      { label: "Edinburgh",    region: "edinburgh"    },
      { label: "Highlands",    region: "highlands"    },
      { label: "Loch Lomond",  region: "loch-lomond"  },
      { label: "Isle of Skye", region: "isle-of-skye" },
      { label: "Perthshire",   region: "perthshire"   },
      { label: "St Andrews",   region: "st-andrews"   },
    ],
  },
  {
    slug:         "usa",
    displayLabel: "the USA",
    regions: [
      { label: "New York",     region: "new-york"     },
      { label: "California",   region: "california"   },
      { label: "New England",  region: "new-england"  },
      { label: "Hawaii",       region: "hawaii"       },
      { label: "Colorado",     region: "colorado"     },
      { label: "Nashville",    region: "nashville"    },
    ],
  },
  {
    slug:         "ireland",
    displayLabel: "Ireland",
    regions: [
      { label: "Dublin",     region: "dublin"     },
      { label: "Wicklow",    region: "wicklow"    },
      { label: "Kerry",      region: "kerry"      },
      { label: "Galway",     region: "galway"     },
      { label: "Clare",      region: "clare"      },
      { label: "Kilkenny",   region: "kilkenny"   },
    ],
  },
  {
    slug:         "switzerland",
    displayLabel: "Switzerland",
    regions: [
      { label: "Zurich",     region: "zurich"     },
      { label: "Geneva",     region: "geneva"     },
      { label: "Interlaken", region: "interlaken" },
      { label: "Lugano",     region: "lugano"     },
      { label: "Zermatt",    region: "zermatt"    },
    ],
  },
  {
    slug:         "austria",
    displayLabel: "Austria",
    regions: [
      { label: "Vienna",    region: "vienna"   },
      { label: "Salzburg",  region: "salzburg" },
      { label: "Tyrol",     region: "tyrol"    },
      { label: "Carinthia", region: "carinthia"},
    ],
  },
  {
    slug:         "cyprus",
    displayLabel: "Cyprus",
    regions: [
      { label: "Paphos",    region: "paphos"    },
      { label: "Limassol",  region: "limassol"  },
      { label: "Nicosia",   region: "nicosia"   },
      { label: "Ayia Napa", region: "ayia-napa" },
    ],
  },
  {
    slug:         "turkey",
    displayLabel: "Turkey",
    regions: [
      { label: "Istanbul",   region: "istanbul"   },
      { label: "Bodrum",     region: "bodrum"     },
      { label: "Cappadocia", region: "cappadocia" },
      { label: "Antalya",    region: "antalya"    },
    ],
  },
  {
    slug:         "uae",
    displayLabel: "the UAE",
    regions: [
      { label: "Dubai",          region: "dubai"     },
      { label: "Abu Dhabi",      region: "abu-dhabi" },
      { label: "Ras Al Khaimah", region: "rak"       },
    ],
  },
  {
    slug:         "south-africa",
    displayLabel: "South Africa",
    regions: [
      { label: "Cape Town",    region: "cape-town"    },
      { label: "Stellenbosch", region: "stellenbosch" },
      { label: "Franschhoek",  region: "franschhoek"  },
      { label: "Garden Route",  region: "garden-route" },
      { label: "Johannesburg",  region: "johannesburg" },
    ],
  },
  {
    slug:         "mexico",
    displayLabel: "Mexico",
    regions: [
      { label: "Riviera Maya",   region: "riviera-maya"   },
      { label: "Los Cabos",      region: "los-cabos"      },
      { label: "Oaxaca",         region: "oaxaca"         },
      { label: "Mexico City",    region: "mexico-city"    },
      { label: "Puerto Vallarta",region: "puerto-vallarta"},
    ],
  },
];

// ── Config builder ─────────────────────────────────────────────────────────────
// Merges a country's region row with the two shared rows.
// Returns null if the country slug isn't in the registry (graceful fallback).
function buildCountryConfig(slug) {
  const entry = COUNTRY_REGISTRY.find(c => c.slug === slug);
  if (!entry) return null;
  return {
    displayLabel: entry.displayLabel,
    rows: [
      {
        rowLabel:      "Region",
        summaryFormat: "title",
        chips:         entry.regions,
      },
      SHARED_WEDDING_TYPE_ROW,
      entry.venueStyle
        ? { ...SHARED_VENUE_STYLE_ROW, chips: entry.venueStyle }
        : SHARED_VENUE_STYLE_ROW,
    ],
  };
}

// ── Summary format helper ──────────────────────────────────────────────────────
// "title"         → label as-is          e.g. "Cotswolds"
// "sentence"      → first-cap only       e.g. "Country wedding"
// "title-setting" → label + " setting"   e.g. "Castle setting"
function applySummaryFormat(label, format) {
  if (format === "sentence") {
    const s = label.toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (format === "title-setting") return `${label} setting`;
  return label;
}

export default function ImmersiveSearch({
  isOpen,
  onClose,
  onViewRegionCategory,
  onViewRegion,
  onViewCategory,
  initialLocation = null,  // { label, countrySlug, regionSlug }, pre-fills location + skips to step 1
  initialQuery    = null,  // free-text from hero AI bar, skips all steps, goes straight to Aura chat
}) {
  const [mounted,       setMounted]       = useState(false); // controls CSS opacity
  const [step,          setStep]          = useState(0);     // 0 = location, 1 = category
  const [stepIn,        setStepIn]        = useState(true);  // step transition visibility
  const [location,      setLocation]      = useState(null);  // { label, countrySlug, regionSlug }
  const [query,         setQuery]         = useState("");
  const [suggestions,   setSuggestions]   = useState([]);
  const [liveCountries, setLiveCountries] = useState(() => readLiveCache());
  const [hovTile,       setHovTile]       = useState(null);
  const [hovCat,        setHovCat]        = useState(null);
  const [hovChip,       setHovChip]       = useState(null);
  const [rowSelections, setRowSelections] = useState({}); // { rowIndex: chip }, multi-select rows
  const [contextChip,   setContextChip]   = useState(null); // flat chip mode (Italy etc.)
  const [suggOpen,      setSuggOpen]      = useState(false);
  const [inputFocused,  setInputFocused]  = useState(false);
  const [destHint,      setDestHint]      = useState(false);
  const [exitingCat,    setExitingCat]    = useState(null);
  const [auraMode,      setAuraMode]      = useState(false);
  const [auraQuery,     setAuraQuery]     = useState("");
  const [auraFocused,   setAuraFocused]   = useState(false);
  const [auraChatOpen,  setAuraChatOpen]  = useState(false);
  const [auraChatQuery, setAuraChatQuery] = useState("");

  // Step 2, refinement
  const [pendingCat,    setPendingCat]    = useState(null); // category chosen in step 1
  const [refStyle,      setRefStyle]      = useState(null);
  const [refGuests,     setRefGuests]     = useState(null);
  const [refSetting,    setRefSetting]    = useState(null);
  const [refBudget,     setRefBudget]     = useState(null);
  const [transitioning,   setTransitioning]   = useState(false);
  const [curatingLineIdx, setCuratingLineIdx] = useState(0);
  const auraInputRef = useRef(null);
  const inputRef = useRef(null);

  // Featured destination tiles from geo data
  const featuredTiles = FEATURED_SLUGS
    .map((s) => GEO_COUNTRIES.find((c) => c.slug === s))
    .filter(Boolean);

  // Re-derived on each open so fresh click counts are reflected
  const featuredRegionTiles = buildFeaturedRegionTiles();

  // ── Open / close lifecycle ────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStepIn(true);
      setQuery("");
      setContextChip(null);
      setRowSelections({});
      setSuggestions([]);
      setPendingCat(null);
      setRefStyle(null);
      setRefGuests(null);
      setRefSetting(null);
      setRefBudget(null);

      if (initialQuery) {
        const parsedLoc = parseLocationFromQuery(initialQuery);
        const parsedCat = parseCategoryFromQuery(initialQuery);

        if (parsedLoc) {
          // Location found, set context and open Aura chat with the full query.
          // ASK AURA always means Aura: couples chose intelligence over Browse.
          // categorySlug flows into activeContext so Aura fetches real DB listings.
          setLocation({ ...parsedLoc, categorySlug: parsedCat || null });
          setStep(1);
          setAuraMode(false);
          setAuraQuery(initialQuery);
          setAuraChatQuery(initialQuery);
          setAuraChatOpen(true);
        } else {
          // No location, open Aura chat directly
          setStep(0);
          setLocation(null);
          setAuraMode(false);
          setAuraQuery(initialQuery);
          setAuraChatQuery(initialQuery);
          setAuraChatOpen(true);
        }
      } else if (initialLocation) {
        setLocation(initialLocation);
        setStep(initialLocation.regionSlug ? 1 : 0);
        setAuraMode(false);
        setAuraQuery("");
        setAuraChatOpen(false);
      } else if (useAura.getState().wedding) {
        // Returning couple with a wedding → land straight on their wedding canvas,
        // never the "Where are you dreaming of?" discovery wizard. The wizard is for
        // new visitors with no wedding yet.
        setStep(0);
        setLocation(null);
        setAuraMode(false);
        setAuraQuery("");
        setAuraChatOpen(true);
      } else {
        setStep(0);
        setLocation(null);
        setAuraMode(false);
        setAuraQuery("");
        setAuraChatOpen(false);
      }

      const t = setTimeout(() => setMounted(true), 16);
      return () => clearTimeout(t);
    } else {
      setMounted(false);
    }
  }, [isOpen]); // eslint-disable-line

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  // Auto-focus input when on location step
  useEffect(() => {
    if (isOpen && mounted && step === 0 && !auraMode) {
      const t = setTimeout(() => inputRef.current?.focus(), 320);
      return () => clearTimeout(t);
    }
  }, [isOpen, mounted, step, auraMode]);

  // Auto-focus Aura input when aura mode activates
  useEffect(() => {
    if (auraMode) {
      const t = setTimeout(() => auraInputRef.current?.focus(), 320);
      return () => clearTimeout(t);
    }
  }, [auraMode]);


  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") handleClose(); };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]); // eslint-disable-line

  // ── Live locations from Supabase ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !supabase || readLiveCache()) return;
    supabase
      .from("listings")
      .select("country, country_slug")
      .eq("status", "published")
      .then(({ data }) => {
        if (!data?.length) return;
        const seen = new Set();
        const out  = [];
        const cap  = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
        data.forEach((r) => {
          const key = (r.country_slug || r.country || "").toLowerCase();
          if (key && !seen.has(key)) {
            seen.add(key);
            out.push({ label: cap(r.country || r.country_slug), slug: r.country_slug || key });
          }
        });
        const sorted = out.sort((a, b) => a.label.localeCompare(b.label));
        writeLiveCache(sorted);
        setLiveCountries(sorted);
      })
      .catch(() => {}); // silent, falls back to STATIC_LOCATIONS
  }, [isOpen]); // eslint-disable-line

  // ── Suggestion matching ───────────────────────────────────────────────────
  useEffect(() => {
    const q = query.toLowerCase().trim();
    const results = buildSuggestions(q, liveCountries);
    setSuggestions(results);
    setSuggOpen(results.length > 0 && q.length > 0);
  }, [query, liveCountries]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setMounted(false);
    setTimeout(() => {
      onClose?.();
      setStep(0);
      setLocation(null);
      setQuery("");
    }, 460);
  }, [onClose]);

  const goToCategory = useCallback((loc) => {
    if (loc && !loc.countrySlug) {
      // Destination typed but has no route yet, show inline hint, don't advance
      setDestHint(true);
      setTimeout(() => setDestHint(false), 3500);
      return;
    }
    setDestHint(false);
    setLocation(loc || null);
    // Step-out animation then switch
    setStepIn(false);
    setTimeout(() => {
      setStep(1);
      setStepIn(true);
    }, 320);
  }, []);

  const goBackToLocation = useCallback(() => {
    setStepIn(false);
    setTimeout(() => {
      setStep(0);
      setStepIn(true);
      setLocation(null);
      setQuery("");
    }, 320);
  }, []);

  const openAuraMode = useCallback(() => {
    setStepIn(false);
    setTimeout(() => { setAuraMode(true); setStepIn(true); }, 320);
  }, []);

  const handleAuraSubmit = useCallback(() => {
    if (!auraQuery.trim()) return;
    const q = auraQuery.trim();
    setAuraChatQuery(q);
    setAuraChatOpen(true);
  }, [auraQuery]);

  const handleAuraChatClose = useCallback(() => {
    setAuraChatOpen(false);
    setAuraChatQuery("");
    // Return to the aura describe step
    setAuraMode(true);
    setMounted(true);
  }, []);

  const handleTileSelect    = (tile) => goToCategory({ label: tile.name, countrySlug: tile.slug, regionSlug: null });
  const handleSkipLocation  = ()     => goToCategory(null);
  const handleSuggSelect    = (sug)  => { setQuery(""); setSuggOpen(false); goToCategory(sug); };

  // ── Row-based multi-select (England / grouped rows config) ──────────────────
  // Toggles selection within a row, one chip active per row, click again to deselect.
  // Does NOT auto-advance. User commits via handleContinueFromChips.
  const handleRowChipToggle = useCallback((chip, rowIndex) => {
    setRowSelections(prev => {
      if (prev[rowIndex]?.label === chip.label) {
        const next = { ...prev };
        delete next[rowIndex];
        return next;
      }
      return { ...prev, [rowIndex]: chip };
    });
  }, []);

  // Applies all row selections and advances to category step.
  // Uses buildCountryConfig to recover the row schema so applySummaryFormat
  // produces the same natural-language label as the selection summary strip.
  const handleContinueFromChips = useCallback(() => {
    const config = buildCountryConfig(initialLocation?.countrySlug);
    const rows   = config?.rows || [];
    const selected = Object.values(rowSelections);

    selected.forEach(chip => {
      if (chip.style)   setRefStyle(chip.style);
      if (chip.setting) setRefSetting(chip.setting);
      if (chip.guests)  setRefGuests(chip.guests);
    });

    const regionChip      = selected.find(c => c.region);
    const updatedLocation = regionChip
      ? { ...initialLocation, label: regionChip.label, regionSlug: regionChip.region }
      : initialLocation;

    // Build formatted summary, row-ordered, same format as the selection strip
    const summary = rows
      .map((row, i) => rowSelections[i]
        ? applySummaryFormat(rowSelections[i].label, row.summaryFormat)
        : null)
      .filter(Boolean)
      .join("  ·  ");

    setContextChip(summary || null);
    setStepIn(false);
    setTimeout(() => {
      setLocation(updatedLocation);
      setStep(1);
      setStepIn(true);
    }, 280);
  }, [rowSelections, initialLocation]);

  // handleIconCatSelect, used by the icon-grid category picker in step 1.
  // Falls back to onViewCategory with a countrySlug object when no region is set
  // so we never call onViewRegionCategory with a null region.
  const handleIconCatSelect = useCallback((vc) => {
    const countrySlug = location?.countrySlug || null;
    const regionSlug  = location?.regionSlug  || null;
    handleClose();
    setTimeout(() => {
      if (countrySlug && regionSlug) {
        onViewRegionCategory?.(countrySlug, regionSlug, vc.slug);
      } else if (countrySlug) {
        onViewCategory?.({ category: vc.slug, countrySlug });
      } else {
        onViewCategory?.({ category: vc.slug });
      }
    }, 500);
  }, [location, handleClose, onViewRegionCategory, onViewCategory]);

  const handleCategorySelect = useCallback((cat) => {
    // Phase 1: card lifts + others fade
    setExitingCat(cat.id);
    // Phase 2: transition to step 2
    setTimeout(() => {
      setPendingCat(cat);
      setExitingCat(null);
      setStepIn(false);
      setTimeout(() => { setStep(2); setStepIn(true); }, 320);
    }, 420);
  }, []);

  const handleStep2Navigate = useCallback(() => {
    if (!pendingCat) return;
    const catSlug     = pendingCat.slug;
    const countrySlug = location?.countrySlug || null;
    const regionSlug  = location?.regionSlug  || null;

    // Store refinements so the results page can pre-apply them
    const refinement = {
      style:   refStyle   || null,
      guests:  refGuests  || null,
      setting: refSetting || null,
      budget:  refBudget  || null,
    };
    if (Object.values(refinement).some(Boolean)) {
      sessionStorage.setItem("lwd:immersive-refinement", JSON.stringify(refinement));
    } else {
      sessionStorage.removeItem("lwd:immersive-refinement");
    }

    // ── Transition frame: hold for 6000ms then melt away (outer opacity fade = 0.45s)
    setTransitioning(true);
    setTimeout(() => {
      setMounted(false); // triggers the 0.45s ease fade on the whole overlay
      setTimeout(() => {
        setTransitioning(false);
        onClose?.();
        setStep(0);
        setLocation(null);
        setQuery("");
        setPendingCat(null);
        setRefStyle(null); setRefGuests(null); setRefSetting(null); setRefBudget(null);
        // Never pass null as regionSlug to onViewRegionCategory.
        // Country-only intent routes through onViewCategory with countrySlug in the object.
        if (countrySlug && regionSlug && catSlug) {
          onViewRegionCategory?.(countrySlug, regionSlug, catSlug);
        } else if (countrySlug && catSlug) {
          onViewCategory?.({ category: catSlug, countrySlug });
        } else {
          onViewCategory?.({ category: catSlug });
        }
      }, 500); // wait for the 0.45s fade to finish
    }, 6000);
  }, [pendingCat, location, refStyle, refGuests, refSetting, refBudget, onClose, onViewRegionCategory, onViewCategory]);

  // ── Cycle curating copy while transitioning ──────────────────────────────
  useEffect(() => {
    if (!transitioning) { setCuratingLineIdx(0); return; }
    const t = setInterval(() => setCuratingLineIdx(i => (i + 1) % 4), 2200);
    return () => clearInterval(t);
  }, [transitioning]);

  // ── Don't mount at all when closed ───────────────────────────────────────
  if (!isOpen) return null;

  // ── Shared step transition style ─────────────────────────────────────────
  const stepTransition = {
    opacity:       stepIn ? 1 : 0,
    transform:     stepIn ? "translateY(0)" : "translateY(28px)",
    transition:    "opacity 0.4s ease, transform 0.55s cubic-bezier(0.16, 1, 0.3, 1)",
    flex:          1,
    display:       "flex",
    flexDirection: "column",
    padding:       "0 clamp(24px, 5vw, 72px) 72px",
    maxWidth:      1100,
    width:         "100%",
    margin:        "0 auto",
    boxSizing:     "border-box",
    position:      "relative",
    zIndex:        1,
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Immersive search"
      style={{
        position:      "fixed",
        inset:         0,
        zIndex:        10000,
        background:    BG,
        display:       "flex",
        flexDirection: "column",
        overflowY:     "auto",
        opacity:       mounted ? 1 : 0,
        transition:    "opacity 0.45s ease",
      }}
    >
      {/* ── Cinematic background motion ──────────────────────────────────── */}
      <style>{`
        @keyframes lwd-bg-drift {
          0%   { transform: translate(0%,    0%)    scale(1.12); }
          33%  { transform: translate(-2%,   1.5%)  scale(1.12); }
          66%  { transform: translate(1.5%,  -1%)   scale(1.12); }
          100% { transform: translate(0%,    0%)    scale(1.12); }
        }
        @keyframes lwd-curating-in {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes lwd-curating-glow {
          0%, 100% { opacity: 0.5;  transform: scale(0.9);  text-shadow: 0 0 12px rgba(211,154,33,0.3); }
          50%      { opacity: 1;    transform: scale(1.15); text-shadow: 0 0 28px rgba(211,154,33,0.75), 0 0 52px rgba(211,154,33,0.3); }
        }
        @keyframes lwd-dot-step {
          0%, 100% { opacity: 0.18; transform: scale(0.7); }
          50%      { opacity: 0.85; transform: scale(1.1); }
        }
        @keyframes lwd-line-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>

      {/* ── Aura transition frame ─────────────────────────────────────────── */}
      {transitioning && (
        <div style={{
          position:        "absolute",
          inset:           0,
          zIndex:          20,
          background:      "rgba(10,9,6,0.96)",
          backdropFilter:  "blur(2px)",
          display:         "flex",
          flexDirection:   "column",
          alignItems:      "center",
          justifyContent:  "center",
          animation:       "lwd-curating-in 0.4s cubic-bezier(0.16,1,0.3,1) both",
        }}>

          {/* Pulsing glow ✦ */}
          <span style={{
            display:    "block",
            color:      GOLD,
            fontSize:   24,
            animation:  "lwd-curating-glow 2.2s ease-in-out infinite",
            marginBottom: 32,
          }}>✦</span>

          {/* Main heading, staggered fade in */}
          <h2 style={{
            fontFamily:    GD,
            fontWeight:    300,
            fontSize:      "clamp(26px, 3.5vw, 44px)",
            color:         WHITE,
            margin:        0,
            letterSpacing: "-0.02em",
            textAlign:     "center",
            lineHeight:    1.1,
            animation:     "lwd-curating-in 0.5s cubic-bezier(0.16,1,0.3,1) 0.1s both",
          }}>
            Aura is curating your results
          </h2>

          {/* Rotating subtitle, re-animates on each line change via key */}
          <p
            key={curatingLineIdx}
            style={{
              fontFamily:    NU,
              fontSize:      12,
              color:         "rgba(245,240,232,0.72)",
              margin:        "14px 0 0",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              textAlign:     "center",
              animation:     "lwd-line-in 0.45s ease both",
            }}
          >
            {[
              (cat) => `Finding ${cat} aligned with your vision`,
              (cat) => `Curating your perfect ${cat} selection`,
              (cat) => `Refining your ${cat} shortlist`,
              (cat) => `Matching ${cat} to your vision`,
            ][curatingLineIdx](pendingCat?.label?.toLowerCase() || "results")}
          </p>

          {/* Dot progression */}
          <div style={{ display: "flex", gap: 7, marginTop: 36, animation: "lwd-curating-in 0.5s ease 0.3s both" }}>
            {[0, 1, 2].map(i => (
              <span key={i} style={{
                display:          "block",
                width:            4,
                height:           4,
                borderRadius:     "50%",
                background:       GOLD,
                animation:        `lwd-dot-step 1.6s ease-in-out ${i * 0.28}s infinite`,
              }} />
            ))}
          </div>

        </div>
      )}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <div style={{
          position:   "absolute",
          inset:      "-12%",
          background: [
            "radial-gradient(ellipse 72% 58% at 20% 30%, rgba(62,36,6,0.72) 0%, transparent 62%)",
            "radial-gradient(ellipse 58% 48% at 82% 28%, rgba(52,30,5,0.55) 0%, transparent 56%)",
            "radial-gradient(ellipse 50% 42% at 85% 72%, rgba(42,24,4,0.5)  0%, transparent 58%)",
            "radial-gradient(ellipse 40% 35% at 50% 52%, rgba(22,13,3,0.3)  0%, transparent 54%)",
          ].join(", "),
          animation:  "lwd-bg-drift 22s ease-in-out infinite",
        }} />
      </div>

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div style={{
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "space-between",
        padding:         "28px clamp(24px, 5vw, 72px)",
        flexShrink:      0,
        position:        "relative",
        zIndex:          1,
      }}>
        {step === 1 ? (
          <button
            onClick={goBackToLocation}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(245,240,232,0.38)", fontFamily: NU, fontSize: 11,
              letterSpacing: "0.14em", textTransform: "uppercase",
              display: "flex", alignItems: "center",
              gap: 10, padding: "6px 0", transition: "color 0.25s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = WHITE)}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,240,232,0.38)")}
          >
            <span style={{ fontSize: 13, letterSpacing: 0, opacity: 0.7 }}>←</span>
            {location?.label ?? "Back"}
          </button>
        ) : (
          <span style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.2em", color: "rgba(245,240,232,0.18)", textTransform: "uppercase" }}>
            Luxury Wedding Directory
          </span>
        )}

        <button
          onClick={handleClose}
          aria-label="Close immersive search"
          style={{
            background: "none",
            border: "1px solid rgba(245,240,232,0.1)",
            borderRadius: 2,
            cursor: "pointer",
            color: "rgba(245,240,232,0.38)",
            fontSize: 18,
            lineHeight: 1,
            padding: "7px 12px",
            letterSpacing: "0.04em",
            fontFamily: NU,
            transition: "color 0.25s ease, border-color 0.25s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = WHITE; e.currentTarget.style.borderColor = "rgba(245,240,232,0.28)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(245,240,232,0.38)"; e.currentTarget.style.borderColor = "rgba(245,240,232,0.1)"; }}
        >
          ×
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          STEP 0, LOCATION
      ═══════════════════════════════════════════════════════════════════ */}
      {step === 0 && !auraMode && !auraChatOpen && (() => {
        const countryCtx    = !!(initialLocation?.countrySlug && !initialLocation?.regionSlug);
        const countryConfig = countryCtx ? buildCountryConfig(initialLocation.countrySlug) : null;
        const groupedRows   = countryConfig?.rows || [];
        const hasRows       = groupedRows.length > 0;
        const displayLabel  = countryConfig?.displayLabel || initialLocation?.label || "";
        const auraPlaceholder = countryCtx
          ? `e.g. "A ${groupedRows[2]?.chips[0]?.label.toLowerCase() || "manor house"} in ${displayLabel} for 80 guests"`
          : "Type a country or city…";

        // Row chip renderer (multi-select, toggles, does NOT auto-advance)
        const renderRowChip = (chip, rowIndex) => {
          const selected = rowSelections[rowIndex]?.label === chip.label;
          const hov      = !selected && hovChip === chip.label;
          return (
            <button
              key={chip.label}
              onClick={() => handleRowChipToggle(chip, rowIndex)}
              onMouseEnter={() => setHovChip(chip.label)}
              onMouseLeave={() => setHovChip(null)}
              style={{
                background:    selected ? "rgba(211,154,33,0.18)" : hov ? "rgba(211,154,33,0.09)" : "rgba(211,154,33,0.04)",
                border:        `1px solid ${selected ? GOLD : hov ? "rgba(211,154,33,0.5)" : "rgba(211,154,33,0.18)"}`,
                borderRadius:  2,
                padding:       "10px 20px",
                cursor:        "pointer",
                color:         selected ? GOLD : hov ? "rgba(245,240,232,0.9)" : "rgba(245,240,232,0.65)",
                fontFamily:    NU,
                fontSize:      13,
                fontWeight:    selected ? 600 : 500,
                letterSpacing: "0.04em",
                transition:    "all 0.2s ease",
                transform:     selected ? "translateY(-1px)" : hov ? "translateY(-1px)" : "none",
                whiteSpace:    "nowrap",
                boxShadow:     selected ? "0 0 0 1px rgba(211,154,33,0.15), 0 4px 16px rgba(211,154,33,0.08)" : "none",
                display:       "inline-flex",
                alignItems:    "center",
                gap:           selected ? 7 : 0,
              }}
            >
              {selected && <span style={{ fontSize: 10, lineHeight: 1 }}>✦</span>}
              {chip.label}
            </button>
          );
        };

        // Derive selection summary, preserves row order and applies natural formatting
        const selectionCount   = Object.keys(rowSelections).length;
        const selectionSummary = groupedRows
          .map((row, i) => rowSelections[i]
            ? applySummaryFormat(rowSelections[i].label, row.summaryFormat)
            : null)
          .filter(Boolean)
          .join("  ·  ");

        return (
        <div style={stepTransition}>

          {/* Heading */}
          <div style={{ marginBottom: countryCtx ? 36 : 52 }}>
            <p style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase", margin: "0 0 18px" }}>
              {countryCtx ? "Step 1 of 2" : "Step 1 of 3"}
            </p>
            <h1 style={{
              fontFamily: GD, fontWeight: 400, margin: 0, color: WHITE,
              fontSize: "clamp(38px, 6vw, 76px)",
              lineHeight: 1.04, letterSpacing: "-0.025em",
            }}>
              {countryCtx
                ? <>Plan your luxury<br />weddings in <span style={{ color: GOLD, fontStyle: "italic" }}>{displayLabel}</span></>
                : <>Where are you<br />dreaming of?</>
              }
            </h1>
            {!countryCtx && (
              <p style={{ fontFamily: NU, fontSize: 15, color: MUTED, margin: "18px 0 0" }}>
                Choose a destination or type below
              </p>
            )}
          </div>

          {/* ── COUNTRY CONTEXT: grouped rows (multi-select) ───────────────── */}
          {countryCtx && hasRows && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 28, marginBottom: 36 }}>
                {groupedRows.map((row, rowIndex) => (
                  <div key={row.rowLabel}>
                    <p style={{
                      fontFamily: NU, fontSize: 9, letterSpacing: "0.24em",
                      textTransform: "uppercase", color: "rgba(211,154,33,0.5)",
                      margin: "0 0 12px",
                    }}>
                      {row.rowLabel}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {row.chips.map(chip => renderRowChip(chip, rowIndex))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Selection summary + Continue */}
              <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 40 }}>
                {/* Summary strip */}
                <div style={{ flex: 1, minHeight: 38 }}>
                  <p style={{
                    fontFamily: NU, fontSize: 9, letterSpacing: "0.22em",
                    textTransform: "uppercase", color: "rgba(211,154,33,0.38)",
                    margin: "0 0 6px",
                  }}>
                    Building your selection
                  </p>
                  <p style={{
                    fontFamily:    NU,
                    fontSize:      12,
                    color:         selectionCount > 0 ? "rgba(211,154,33,0.85)" : "rgba(211,154,33,0.2)",
                    margin:        0,
                    letterSpacing: "0.06em",
                    transition:    "color 0.3s ease",
                    minHeight:     16,
                  }}>
                    {selectionCount > 0 ? `✦  ${selectionSummary}` : "-"}
                  </p>
                </div>

                {/* Continue button */}
                <button
                  onClick={handleContinueFromChips}
                  style={{
                    display:       "inline-flex",
                    alignItems:    "center",
                    gap:           10,
                    background:    selectionCount > 0 ? "rgba(211,154,33,0.14)" : "rgba(211,154,33,0.05)",
                    border:        `1px solid ${selectionCount > 0 ? "rgba(211,154,33,0.7)" : "rgba(211,154,33,0.2)"}`,
                    borderRadius:  2,
                    padding:       "13px 28px",
                    cursor:        "pointer",
                    color:         selectionCount > 0 ? GOLD : "rgba(211,154,33,0.35)",
                    fontFamily:    NU,
                    fontSize:      12,
                    fontWeight:    600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    transition:    "all 0.25s ease",
                    whiteSpace:    "nowrap",
                  }}
                  onMouseEnter={e => {
                    if (selectionCount > 0) {
                      e.currentTarget.style.background   = "rgba(211,154,33,0.22)";
                      e.currentTarget.style.borderColor  = GOLD;
                      e.currentTarget.style.color        = WHITE;
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background  = selectionCount > 0 ? "rgba(211,154,33,0.14)" : "rgba(211,154,33,0.05)";
                    e.currentTarget.style.borderColor = selectionCount > 0 ? "rgba(211,154,33,0.7)"  : "rgba(211,154,33,0.2)";
                    e.currentTarget.style.color       = selectionCount > 0 ? GOLD : "rgba(211,154,33,0.35)";
                  }}
                >
                  {selectionCount > 0 ? "Refine your selection" : "Skip"}
                  <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
                </button>
              </div>
            </>
          )}


          {/* ── NORMAL MODE: featured destination tiles + region tiles ─────── */}
          {!countryCtx && (
            <>
              {/* Country tiles */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                {featuredTiles.map((tile) => {
                  const hov = hovTile === tile.slug;
                  const img = TILE_IMAGES[tile.slug];
                  return (
                    <button
                      key={tile.slug}
                      onClick={() => handleTileSelect(tile)}
                      onMouseEnter={() => setHovTile(tile.slug)}
                      onMouseLeave={() => setHovTile(null)}
                      style={{
                        position:     "relative",
                        overflow:     "hidden",
                        background:   CARD,
                        border:       `1px solid ${hov ? "rgba(211,154,33,0.45)" : "rgba(255,255,255,0.06)"}`,
                        borderRadius: 2,
                        padding:      "16px 26px",
                        cursor:       "pointer",
                        transition:   "border-color 0.35s ease, transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s ease",
                        transform:    hov ? "translateY(-4px) scale(1.02)" : "translateY(0) scale(1)",
                        boxShadow:    hov ? "0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(211,154,33,0.12)" : "none",
                        display:      "flex",
                        alignItems:   "center",
                        gap:          10,
                      }}
                    >
                      {img && (
                        <div style={{
                          position: "absolute", inset: 0,
                          backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center",
                          opacity: hov ? 1 : 0, transform: hov ? "scale(1)" : "scale(1.08)",
                          transition: "opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)",
                        }} />
                      )}
                      <div style={{
                        position: "absolute", inset: 0,
                        background: hov ? "linear-gradient(135deg,rgba(0,0,0,0.68) 0%,rgba(0,0,0,0.52) 100%)" : "linear-gradient(135deg,rgba(10,9,6,0.95) 0%,rgba(10,9,6,0.88) 100%)",
                        transition: "background 0.4s ease",
                      }} />
                      <span style={{ position: "relative", zIndex: 1, fontFamily: GD, fontSize: 20, color: WHITE, fontWeight: 400, letterSpacing: "0.01em" }}>{tile.name}</span>
                      <span style={{ position: "relative", zIndex: 1, color: hov ? GOLD : "rgba(211,154,33,0.3)", fontSize: 14, transition: "color 0.25s ease, transform 0.4s cubic-bezier(0.16,1,0.3,1)", transform: hov ? "translateX(3px)" : "none", display: "inline-block" }}>→</span>
                    </button>
                  );
                })}
              </div>

              {/* Thin divider between country and region tiles */}
              <div style={{ width: "100%", height: 1, background: "rgba(211,154,33,0.1)", margin: "0 0 12px" }} />

              {/* Region tiles, visually lighter than country tiles */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {featuredRegionTiles.map((region) => {
                  const hov = hovTile === region.slug;
                  const img = REGION_TILE_IMAGES[region.slug];
                  const countryName = GEO_COUNTRIES.find((c) => c.slug === region.countrySlug)?.name || region.countrySlug;
                  return (
                    <button
                      key={region.slug}
                      onClick={() => {
                        recordRegionClick(region.slug);
                        goToCategory({ label: region.name, countrySlug: region.countrySlug, regionSlug: region.slug });
                      }}
                      onMouseEnter={() => setHovTile(region.slug)}
                      onMouseLeave={() => setHovTile(null)}
                      style={{
                        position:      "relative",
                        overflow:      "hidden",
                        background:    CARD,
                        border:        `1px solid ${hov ? "rgba(211,154,33,0.38)" : "rgba(255,255,255,0.05)"}`,
                        borderRadius:  2,
                        padding:       "10px 18px",
                        cursor:        "pointer",
                        transition:    "border-color 0.35s ease, transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s ease",
                        transform:     hov ? "translateY(-3px) scale(1.015)" : "translateY(0) scale(1)",
                        boxShadow:     hov ? "0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(211,154,33,0.1)" : "none",
                        display:       "flex",
                        flexDirection: "column",
                        alignItems:    "flex-start",
                        gap:           3,
                      }}
                    >
                      {img && (
                        <div style={{
                          position: "absolute", inset: 0,
                          backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center",
                          opacity: hov ? 0.9 : 0, transform: hov ? "scale(1)" : "scale(1.08)",
                          transition: "opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)",
                        }} />
                      )}
                      <div style={{
                        position: "absolute", inset: 0,
                        background: hov ? "linear-gradient(135deg,rgba(0,0,0,0.72) 0%,rgba(0,0,0,0.56) 100%)" : "linear-gradient(135deg,rgba(10,9,6,0.96) 0%,rgba(10,9,6,0.9) 100%)",
                        transition: "background 0.4s ease",
                      }} />
                      <span style={{ position: "relative", zIndex: 1, fontFamily: GD, fontSize: 15, color: hov ? WHITE : "rgba(245,240,232,0.82)", fontWeight: 400, letterSpacing: "0.01em", transition: "color 0.25s ease" }}>{region.name}</span>
                      <span style={{ position: "relative", zIndex: 1, fontFamily: NU, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: hov ? "rgba(211,154,33,0.75)" : "rgba(211,154,33,0.35)", transition: "color 0.25s ease" }}>{countryName}</span>
                    </button>
                  );
                })}
              </div>

              {/* Style-led discovery tiles, Mode 2 */}
              <div style={{ width: "100%", height: 1, background: "rgba(211,154,33,0.07)", margin: "0 0 10px" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontFamily: NU, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(211,154,33,0.35)" }}>Or explore by style</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 44 }}>
                {STYLE_TILES.map((tile) => {
                  const hov = hovTile === tile.id;
                  return (
                    <button
                      key={tile.id}
                      onClick={() => { setAuraChatQuery(tile.query); setAuraChatOpen(true); }}
                      onMouseEnter={() => setHovTile(tile.id)}
                      onMouseLeave={() => setHovTile(null)}
                      style={{
                        background:   hov ? "rgba(211,154,33,0.08)" : "rgba(211,154,33,0.03)",
                        border:       `1px solid ${hov ? "rgba(211,154,33,0.35)" : "rgba(211,154,33,0.12)"}`,
                        borderRadius: 2,
                        padding:      "6px 14px",
                        cursor:       "pointer",
                        transition:   "background 0.25s ease, border-color 0.25s ease",
                        display:      "flex",
                        alignItems:   "center",
                        gap:          6,
                      }}
                    >
                      <span style={{ fontFamily: NU, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: hov ? GOLD : "rgba(211,154,33,0.55)", transition: "color 0.25s ease" }}>{tile.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Search input + suggestions */}
          <div style={{ position: "relative", maxWidth: 520, marginBottom: 28 }}>
            <div style={{
              display:      "flex",
              alignItems:   "center",
              background:   CARD,
              border:       `1px solid ${inputFocused ? "rgba(211,154,33,0.55)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 2,
              boxShadow:    inputFocused
                ? "0 0 0 3px rgba(211,154,33,0.07), 0 8px 32px rgba(0,0,0,0.45)"
                : "0 2px 16px rgba(0,0,0,0.3)",
              transition:   "border-color 0.3s ease, box-shadow 0.35s ease",
            }}>
              <span style={{ paddingLeft: 20, color: inputFocused ? GOLD : MUTED, fontSize: 15, flexShrink: 0, transition: "color 0.3s ease" }}>⌕</span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder={auraPlaceholder}
                style={{
                  flex:          1,
                  background:    "none",
                  border:        "none",
                  outline:       "none",
                  padding:       "18px 20px",
                  color:         WHITE,
                  fontFamily:    NU,
                  fontSize:      16,
                  letterSpacing: "0.015em",
                  caretColor:    GOLD,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && suggestions.length > 0) handleSuggSelect(suggestions[0]);
                }}
              />
              {query && (
                <button
                  onClick={() => { setQuery(""); setSuggOpen(false); inputRef.current?.focus(); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: "0 16px", fontSize: 18, lineHeight: 1 }}
                >×</button>
              )}
            </div>

            {/* Dropdown suggestions */}
            {suggOpen && (
              <div style={{
                position:   "absolute",
                top:        "calc(100% + 4px)",
                left:       0, right:   0,
                background: "#161410",
                border:     `1px solid ${GOLD_D}`,
                borderRadius: 2,
                overflow:   "hidden",
                zIndex:     100,
                boxShadow:  "0 16px 48px rgba(0,0,0,0.6)",
              }}>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onMouseDown={() => handleSuggSelect(s)}
                    style={{
                      display:       "block",
                      width:         "100%",
                      textAlign:     "left",
                      background:    "none",
                      border:        "none",
                      borderBottom:  i < suggestions.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                      padding:       "13px 20px",
                      color:         WHITE,
                      fontFamily:    NU,
                      fontSize:      14,
                      cursor:        "pointer",
                      letterSpacing: "0.01em",
                      transition:    "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = CARD_H)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Destination-not-supported hint */}
          {destHint && (
            <p style={{
              margin: "8px 0 0",
              fontSize: 12,
              color: "#d39a21",
              letterSpacing: "0.04em",
              fontFamily: "inherit",
              opacity: destHint ? 1 : 0,
              transition: "opacity 0.3s",
            }}>
              We don't have listings for that destination yet. Try Italy, France, or the UK.
            </p>
          )}

          {/* Skip */}
          <button
            onClick={handleSkipLocation}
            style={{
              background:      "none",
              border:          "none",
              cursor:          "pointer",
              color:           MUTED,
              fontFamily:      NU,
              fontSize:        13,
              letterSpacing:   "0.06em",
              padding:         0,
              textDecoration:  "underline",
              textDecorationColor: "rgba(245,240,232,0.18)",
              transition:      "color 0.2s",
              alignSelf:       "flex-start",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = WHITE)}
            onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
          >
            Explore all destinations →
          </button>

          {/* Ask Aura, Step 1 */}
          <button
            onClick={openAuraMode}
            style={{
              marginTop:      32,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "space-between",
              width:          "100%",
              maxWidth:       480,
              background:     "rgba(211,154,33,0.07)",
              border:         "1px solid rgba(211,154,33,0.38)",
              borderRadius:   2,
              padding:        "18px 20px",
              cursor:         "pointer",
              boxShadow:      "0 0 32px rgba(211,154,33,0.14), inset 0 0 0 1px rgba(211,154,33,0.06)",
              transition:     "border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(211,154,33,0.65)";
              e.currentTarget.style.background  = "rgba(211,154,33,0.12)";
              e.currentTarget.style.boxShadow   = "0 0 48px rgba(211,154,33,0.22), inset 0 0 0 1px rgba(211,154,33,0.10)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(211,154,33,0.38)";
              e.currentTarget.style.background  = "rgba(211,154,33,0.07)";
              e.currentTarget.style.boxShadow   = "0 0 32px rgba(211,154,33,0.14), inset 0 0 0 1px rgba(211,154,33,0.06)";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ color: GOLD, fontSize: 17, display: "inline-block", animation: "lwd-aura-pulse 2.4s ease-in-out infinite" }}>✦</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontFamily: NU, fontSize: 13, fontWeight: 600, letterSpacing: "0.06em", color: GOLD }}>
                  Ask Aura anything
                </div>
                <div style={{ fontFamily: NU, fontSize: 11, color: "rgba(245,240,232,0.50)", marginTop: 3, letterSpacing: "0.02em" }}>
                  Our AI knows every venue in the collection
                </div>
              </div>
            </div>
            <span style={{ color: "rgba(211,154,33,0.7)", fontSize: 16 }}>→</span>
          </button>
        </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
          STEP 1, CATEGORY
      ═══════════════════════════════════════════════════════════════════ */}
      {step === 1 && !auraMode && !auraChatOpen && (
        <div style={stepTransition}>

          {/* Heading */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase", margin: "0 0 18px" }}>
              {initialLocation?.countrySlug && !initialLocation?.regionSlug ? "Step 2 of 2" : "Step 2 of 3"}
            </p>
            <h1 style={{
              fontFamily: GD, fontWeight: 400, margin: 0, color: WHITE,
              fontSize: "clamp(40px, 6.5vw, 82px)",
              lineHeight: 1.04, letterSpacing: "-0.025em",
            }}>
              What are you<br />planning?
            </h1>
          </div>

          {/* Grid header row, location + chip label */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", maxWidth: 920, marginBottom: 10 }}>
            {location ? (
              <p style={{ fontFamily: NU, fontSize: 11, color: GOLD, margin: 0, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                ✦ {location.label}{contextChip ? ` · ${contextChip}` : ""}
              </p>
            ) : (
              <p style={{ fontFamily: NU, fontSize: 11, color: MUTED, margin: 0, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                All destinations
              </p>
            )}
          </div>

          {/* Category cards */}
          <div style={{
            display:             "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap:                 10,
            maxWidth:            1040,
          }}>
            {CATEGORIES.map((cat) => {
              const hov      = hovCat === cat.id;
              const isExiting = exitingCat === cat.id;
              const otherExiting = exitingCat && exitingCat !== cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat)}
                  onMouseEnter={() => !exitingCat && setHovCat(cat.id)}
                  onMouseLeave={() => !exitingCat && setHovCat(null)}
                  style={{
                    position:     "relative",
                    overflow:     "hidden",
                    background:   CARD,
                    border:       `1px solid ${isExiting ? "rgba(211,154,33,0.6)" : hov ? "rgba(211,154,33,0.45)" : "rgba(255,255,255,0.07)"}`,
                    borderRadius: 2,
                    padding:      "28px 22px",
                    cursor:       exitingCat ? "default" : "pointer",
                    textAlign:    "left",
                    transition:   isExiting
                      ? "transform 0.42s cubic-bezier(0.16,1,0.3,1), box-shadow 0.42s ease, opacity 0.3s ease"
                      : "border-color 0.4s ease, transform 0.45s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease, opacity 0.3s ease",
                    transform:    isExiting
                      ? "translateY(-10px) scale(1.04)"
                      : otherExiting ? "scale(0.97)"
                      : hov ? "translateY(-6px) scale(1.015)"
                      : "translateY(0) scale(1)",
                    opacity:      otherExiting ? 0.35 : 1,
                    boxShadow:    isExiting
                      ? "0 40px 100px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,154,33,0.25)"
                      : hov ? "0 28px 72px rgba(0,0,0,0.7), 0 0 0 1px rgba(211,154,33,0.15)"
                      : "inset 0 1px 0 rgba(255,255,255,0.03)",
                    minHeight:    160,
                  }}
                >
                  {/* Immersive image layer */}
                  <div style={{
                    position:           "absolute",
                    inset:              0,
                    backgroundImage:    `url(${cat.img})`,
                    backgroundSize:     "cover",
                    backgroundPosition: "center",
                    opacity:            (hov || isExiting) ? 1 : 0,
                    transform:          (hov || isExiting) ? "scale(1)" : "scale(1.06)",
                    transition:         "opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)",
                  }} />
                  {/* Dark gradient overlay */}
                  <div style={{
                    position:   "absolute",
                    inset:      0,
                    background: isExiting
                      ? "linear-gradient(160deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.42) 100%)"
                      : hov
                      ? "linear-gradient(160deg, rgba(0,0,0,0.74) 0%, rgba(0,0,0,0.62) 100%)"
                      : "linear-gradient(160deg, rgba(10,9,6,0.96) 0%, rgba(10,9,6,0.9) 100%)",
                    transition: "background 0.45s ease",
                  }} />
                  {/* Content, sits above image layers */}
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontFamily: GD, fontSize: "clamp(26px, 2.8vw, 34px)", color: WHITE, fontWeight: 400, marginBottom: 12, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
                      {cat.label}
                    </div>
                    <div style={{ fontFamily: NU, fontSize: 13, color: hov ? "rgba(245,240,232,0.75)" : MUTED, letterSpacing: "0.02em", lineHeight: 1.5, transition: "color 0.3s ease" }}>
                      {cat.tagline}
                    </div>
                    <div style={{
                      marginTop:     22,
                      display:       "inline-flex",
                      alignItems:    "center",
                      gap:           6,
                      color:         hov ? GOLD : "rgba(211,154,33,0.35)",
                      fontFamily:    NU,
                      fontSize:      11,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      transition:    "color 0.35s ease, transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease",
                      transform:     hov ? "translateX(6px)" : "translateX(0)",
                      opacity:       hov ? 1 : 0.6,
                    }}>
                      <span style={{ fontSize: 16, lineHeight: 1 }}>→</span>
                      {hov && <span>Explore</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Ask Aura, Step 2 */}
          <button
            onClick={openAuraMode}
            style={{
              alignSelf:     "flex-end",
              background:    "none",
              border:        "none",
              cursor:        "pointer",
              padding:       "12px 0 0",
              fontFamily:    NU,
              fontSize:      11,
              letterSpacing: "0.1em",
              color:         "rgba(211,154,33,0.38)",
              transition:    "color 0.25s ease",
              maxWidth:      920,
              width:         "100%",
              textAlign:     "right",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(211,154,33,0.38)")}
          >
            Prefer to describe it? Ask Aura →
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          STEP 2, REFINEMENT
      ═══════════════════════════════════════════════════════════════════ */}
      {step === 2 && !auraMode && !auraChatOpen && (
        <div style={stepTransition}>

          {/* Back */}
          <button
            onClick={() => { setStepIn(false); setTimeout(() => { setStep(1); setStepIn(true); setPendingCat(null); }, 320); }}
            style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(245,240,232,0.35)", fontFamily:NU, fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", display:"flex", alignItems:"center", gap:10, padding:"0 0 40px", transition:"color 0.25s ease" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = WHITE)}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,240,232,0.35)")}
          >
            <span style={{ fontSize:13, opacity:0.7 }}>←</span> Back
          </button>

          {/* Heading */}
          <div style={{ marginBottom: 44 }}>
            <p style={{ fontFamily:NU, fontSize:10, letterSpacing:"0.22em", color:GOLD, textTransform:"uppercase", margin:"0 0 18px" }}>
              Step 3 of 3
            </p>
            <h1 style={{ fontFamily:GD, fontWeight:400, margin:0, color:WHITE, fontSize:"clamp(36px, 5.5vw, 72px)", lineHeight:1.05, letterSpacing:"-0.025em" }}>
              Your perfect<br /><em style={{ fontStyle:"italic", color: GOLD }}>{pendingCat?.label || "wedding"}</em>
            </h1>
            <p style={{ fontFamily:NU, fontSize:15, color:"rgba(245,240,232,0.68)", margin:"16px 0 0", fontWeight:300, letterSpacing:"0.01em" }}>
              All optional. Skip anything that doesn't apply.
            </p>
          </div>

          {/* Refinement rows, dynamic per category */}
          <div style={{ display:"flex", flexDirection:"column", gap:28, maxWidth:760 }}>
            {(REFINEMENT_CONFIG[pendingCat?.id] || REFINEMENT_CONFIG["venues"]).map((row) => {
              const stateMap = { style: refStyle, guests: refGuests, setting: refSetting, budget: refBudget };
              const setterMap = { style: setRefStyle, guests: setRefGuests, setting: setRefSetting, budget: setRefBudget };
              const val = stateMap[row.key];
              const set = setterMap[row.key];
              return (
              <div key={row.key} style={{ display:"flex", flexDirection:"column", gap:12 }}>
                <span style={{ fontFamily:NU, fontSize:10, letterSpacing:"0.2em", color:"rgba(245,240,232,0.55)", textTransform:"uppercase" }}>
                  {row.label}
                </span>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {row.options.map((opt) => {
                    const active = val === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => set(active ? null : opt)}
                        style={{
                          background:    active ? GOLD : "transparent",
                          border:        `1px solid ${active ? GOLD : "rgba(245,240,232,0.25)"}`,
                          borderRadius:  2,
                          padding:       "9px 18px",
                          color:         active ? "#0a0906" : "rgba(245,240,232,0.82)",
                          fontFamily:    NU,
                          fontSize:      13,
                          fontWeight:    active ? 600 : 400,
                          letterSpacing: "0.03em",
                          cursor:        "pointer",
                          transition:    "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = "rgba(211,154,33,0.5)"; e.currentTarget.style.color = WHITE; } }}
                        onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = "rgba(245,240,232,0.25)"; e.currentTarget.style.color = "rgba(245,240,232,0.82)"; } }}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
            })}
          </div>

          {/* CTA row */}
          <div style={{ display:"flex", alignItems:"center", gap:28, marginTop:48 }}>
            <button
              onClick={handleStep2Navigate}
              style={{
                background:    GOLD,
                border:        "none",
                borderRadius:  2,
                padding:       "14px 36px",
                color:         "#0a0906",
                fontFamily:    NU,
                fontSize:      12,
                fontWeight:    700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor:        "pointer",
                transition:    "opacity 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              Show Results →
            </button>
            <button
              onClick={handleStep2Navigate}
              style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(245,240,232,0.52)", fontFamily:NU, fontSize:12, letterSpacing:"0.1em", transition:"color 0.2s ease" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(245,240,232,0.85)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,240,232,0.3)")}
            >
              Skip →
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          AURA MODE, lights-out concierge
      ═══════════════════════════════════════════════════════════════════ */}
      {auraMode && (
        <div style={{
          ...stepTransition,
          justifyContent: "center",
          alignItems:     "flex-start",
          paddingTop:     "clamp(20px, 4vh, 56px)",
        }}>
          {/* ── Ambient blooms ── */}
          {/* Purple bloom, top left */}
          <div aria-hidden="true" style={{
            position: "absolute", top: "-10%", left: "-8%",
            width: 600, height: 500, borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(167,139,250,0.13) 0%, transparent 70%)",
            pointerEvents: "none", zIndex: 0,
          }} />
          {/* Secondary gold bloom, bottom right */}
          <div aria-hidden="true" style={{
            position: "absolute", bottom: "-5%", right: "-5%",
            width: 400, height: 360, borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(211,154,33,0.07) 0%, transparent 70%)",
            pointerEvents: "none", zIndex: 0,
          }} />
          {/* Green accent bloom, mid right */}
          <div aria-hidden="true" style={{
            position: "absolute", top: "40%", right: "-10%",
            width: 300, height: 300, borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(107,163,163,0.06) 0%, transparent 70%)",
            pointerEvents: "none", zIndex: 0,
          }} />


          {/* ── AURA watermark ── */}
          <div aria-hidden="true" style={{
            position:      "absolute",
            top:           "50%",
            left:          "50%",
            transform:     "translate(-50%, -50%)",
            fontFamily:    GD,
            fontSize:      "clamp(160px, 22vw, 320px)",
            fontWeight:    700,
            fontStyle:     "italic",
            color:         WHITE,
            opacity:       0.028,
            letterSpacing: "-8px",
            lineHeight:    1,
            pointerEvents: "none",
            userSelect:    "none",
            whiteSpace:    "nowrap",
            zIndex:        0,
          }}>
            AURA
          </div>

          {/* ── Content (above blooms + watermark) ── */}
          <div style={{ position: "relative", zIndex: 1, width: "100%" }}>

            {/* Back */}
            <button
              onClick={() => { setStepIn(false); setTimeout(() => { setAuraMode(false); setStep(step); setStepIn(true); }, 320); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "rgba(245,240,232,0.28)", fontFamily: NU, fontSize: 11,
                letterSpacing: "0.14em", textTransform: "uppercase",
                display: "flex", alignItems: "center", gap: 10,
                padding: "0 0 36px", transition: "color 0.25s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = WHITE)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,240,232,0.28)")}
            >
              <span style={{ fontSize: 13, opacity: 0.7 }}>←</span> Back
            </button>

            {/* Aura wordmark, purple */}
            <p style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.28em", color: "rgba(124,95,192,0.85)", textTransform: "uppercase", margin: "0 0 20px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#7C5FC0" }}>✦</span> Aura
            </p>

            {/* Heading */}
            <h1 style={{
              fontFamily: GD, fontWeight: 400, margin: "0 0 12px", color: WHITE,
              fontSize: "clamp(40px, 6vw, 80px)",
              lineHeight: 1.04, letterSpacing: "-0.025em",
              maxWidth: 680,
            }}>
              Describe your<br />
              <span style={{ fontStyle: "italic", color: "#e8c97a" }}>perfect wedding</span>
            </h1>
            <p style={{ fontFamily: NU, fontSize: 14, color: MUTED, margin: "0 0 36px", letterSpacing: "0.02em", maxWidth: 480 }}>
              Tell Aura what you're imagining, she'll find it.
            </p>

            {/* Textarea */}
            <div style={{ position: "relative", width: "100%", maxWidth: 680 }}>
              <textarea
                ref={auraInputRef}
                value={auraQuery}
                onChange={(e) => setAuraQuery(e.target.value)}
                onFocus={() => setAuraFocused(true)}
                onBlur={() => setAuraFocused(false)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAuraSubmit(); } }}
                placeholder="A villa in Tuscany for 80 guests in September, with a garden ceremony…"
                rows={4}
                style={{
                  width:         "100%",
                  boxSizing:     "border-box",
                  background:    "rgba(12,10,16,0.65)",
                  border:        `1px solid ${auraFocused ? "rgba(167,139,250,0.55)" : "rgba(167,139,250,0.15)"}`,
                  borderRadius:  3,
                  padding:       "22px 24px 64px",
                  color:         WHITE,
                  fontFamily:    NU,
                  fontSize:      16,
                  lineHeight:    1.65,
                  letterSpacing: "0.01em",
                  caretColor:    "#A78BFA",
                  outline:       "none",
                  resize:        "none",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  boxShadow:     auraFocused
                    ? "0 0 0 3px rgba(167,139,250,0.08), 0 12px 48px rgba(0,0,0,0.5)"
                    : "0 4px 32px rgba(0,0,0,0.4)",
                  transition:    "border-color 0.3s ease, box-shadow 0.35s ease",
                }}
              />
              {/* Submit */}
              <button
                onClick={handleAuraSubmit}
                disabled={!auraQuery.trim()}
                style={{
                  position:      "absolute",
                  bottom:        14,
                  right:         14,
                  background:    auraQuery.trim()
                    ? "linear-gradient(135deg, #7C5FC0, #7C5FC0)"
                    : "rgba(124,95,192,0.12)",
                  border:        "none",
                  borderRadius:  3,
                  padding:       "10px 24px",
                  cursor:        auraQuery.trim() ? "pointer" : "default",
                  color:         auraQuery.trim() ? "#fff" : "rgba(167,139,250,0.35)",
                  fontFamily:    NU,
                  fontSize:      11,
                  fontWeight:    700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  transition:    "background 0.25s ease, color 0.25s ease",
                  boxShadow:     auraQuery.trim() ? "0 4px 20px rgba(124,95,192,0.35)" : "none",
                }}
              >
                Ask Aura →
              </button>
            </div>

            {/* Hint */}
            <p style={{ fontFamily: NU, fontSize: 11, color: "rgba(245,240,232,0.18)", margin: "14px 0 0", letterSpacing: "0.04em" }}>
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </div>
      )}


      {/* ── Subtle bottom progress line ───────────────────────────────────── */}
      <div style={{ position: "fixed", bottom: 0, left: 0, height: 2, background: GOLD_D, width: "100%", pointerEvents: "none" }}>
        <div style={{
          height:     "100%",
          background: GOLD,
          width:      step === 0 ? "50%" : "100%",
          transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }} />
      </div>

      {/* ── Aura Immersive Workspace (opens above this overlay) ──────────── */}
      {auraChatOpen && (
        <AuraImmersiveWorkspace
          initialQuery={auraChatQuery}
          locationContext={location}
          onClose={handleAuraChatClose}
        />
      )}
    </div>
  );
}
